import {
  HOMES_TARGETED_URL_PROPERTY_TYPE,
  REAL_ESTATE_TARGETED_URL_PROPERTY_TYPE,
  getTargetedUrlPropertyTypeSelection,
} from 'utils/listing-query-helper';
import { AVAILABLE_STATUS, Filter, NOT_AVAILABLE_SOLD_STATUS, PENDING_STATUS } from 'contexts/preferences/listing-params/types';
import { AddressType } from '@zoocasa/go-search';
import { LOCALE_COMMON_PATHS } from 'types/locales';

/**
 * Generates structured data for localized heading on the area listings page.
 * 
 * This function returns translation key and interpolation values that can be used
 * with next-i18next to render localized headings based on various filter criteria.
 * 
 * @param params - Object containing:
 *   - areaName: The name of the geographical area
 *   - areaType: The type of address (country, province, city, neighbourhood)
 *   - filter: Filter criteria including rental status, listing status, and home type
 *   - city: Optional city name
 *   - neighbourhood: Optional neighbourhood name
 * 
 * @returns Object with:
 *   - key: Translation key path (e.g., "area_page.heading.for_sale_in")
 *   - values: Object with interpolation values (propertyType, areaName)
 */
interface GenerateLocalizedHeadingParams {
  areaName: string;
  areaType: AddressType;
  filter: Pick<Filter, 'rental' | 'status' | 'homeType'>;
  city?: string;
  neighbourhood?: string;
}

export interface LocalizedHeadingData {
  key: string;
  values: {
    propertyType: string;
    areaName?: string;
  };
}

export default function generateLocalizedHeading({
  areaName,
  areaType,
  filter,
  city,
  neighbourhood,
}: GenerateLocalizedHeadingParams): LocalizedHeadingData {
  const homeTypeSelection = getTargetedUrlPropertyTypeSelection(filter.homeType);
  const baseHomeType = homeTypeSelection === HOMES_TARGETED_URL_PROPERTY_TYPE ? REAL_ESTATE_TARGETED_URL_PROPERTY_TYPE : homeTypeSelection;

  // For non-country pages with "Homes" selection, combine "Real Estate & Homes"
  // We'll use a special marker that the view can detect and translate
  let homeType: string;
  if (areaType !== AddressType.ADDRESS_TYPE_COUNTRY && homeTypeSelection === HOMES_TARGETED_URL_PROPERTY_TYPE) {
    homeType = 'Real Estate & Homes'; // Special case that needs translation
  } else {
    homeType = baseHomeType;
  }

  let areaNameText = areaName;
  if (city && neighbourhood) {
    areaNameText = `${neighbourhood}, ${city}`;
  }

  // Determine translation key based on status
  if (filter.status !== AVAILABLE_STATUS) {
    if (filter.status === NOT_AVAILABLE_SOLD_STATUS) {
      // Sold or Rented
      const key = filter.rental ? LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_RECENTLY_RENTED : LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_RECENTLY_SOLD;
      return {
        key,
        values: {
          propertyType: homeType,
          areaName: areaNameText,
        },
      };
    } else if (filter.status === PENDING_STATUS) {
      // Pending listings, split by sale vs rental: the shared `pending_listings` string
      // previously covered both, so a pending rental read as a pending sale.
      const key = filter.rental ? LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_PENDING_RENTALS : LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_PENDING_LISTINGS;
      return {
        key,
        values: {
          propertyType: homeType,
          areaName: areaNameText,
        },
      };
    } else {
      // Past listings (other statuses), split by sale vs rental for the same reason.
      const key = filter.rental ? LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_PAST_RENTALS : LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_PAST_LISTINGS;
      return {
        key,
        values: {
          propertyType: homeType,
          areaName: areaNameText,
        },
      };
    }
  } else {
    // Available listings (for sale or rent)
    if (areaName) {
      const key = filter.rental ? LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_FOR_RENT_IN : LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_FOR_SALE_IN;
      return {
        key,
        values: {
          propertyType: homeType,
          areaName: areaNameText,
        },
      };
    } else {
      const key = filter.rental ? LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_FOR_RENT : LOCALE_COMMON_PATHS.AREA_PAGE_HEADING_FOR_SALE;
      return {
        key,
        values: {
          propertyType: homeType,
        },
      };
    }
  }
}
